function formatMoney(cents, format = '€{{amount}}') {

    const value = Math.round(cents) / 100;

    const withSeparators = (decimals, thousandsSep, decimalSep) => {
        const [int, frac] = value.toFixed(decimals).split('.');
        const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
        return decimals > 0 ? grouped + decimalSep + frac : grouped;
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
        switch (placeholder) {
            case 'amount_no_decimals':
                return withSeparators(0, ',', '.');
            case 'amount_with_comma_separator':
                return withSeparators(2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
                return withSeparators(0, '.', ',');
            case 'amount_with_apostrophe_separator':
                return withSeparators(2, "'", '.');
            default:
                return withSeparators(2, ',', '.');
        }
    });
}

class FreeShippingMeter extends HTMLElement {

    static texts = window.free_shipping_text;

    /* Campaign schedule (Theme settings > Cart > Campaign schedule). Dates are Madrid
       time and are checked here, in the browser, because the drawer HTML is cached. */
    static startsAt(value) {
        if (!value) return NaN;
        if (window.MTPCampaign && typeof window.MTPCampaign.toEpoch === 'function') {
            return window.MTPCampaign.toEpoch(value);
        }
        return Date.parse(String(value).replace(' ', 'T') + ':00+01:00');
    }

    static get freeshipPrice() {
        const campaign = window.free_shipping_campaign;
        if (campaign && campaign.price > 0) {
            const start = FreeShippingMeter.startsAt(campaign.from);
            if (!isNaN(start) && Date.now() >= start) return campaign.price;
        }
        return parseInt(window.free_shipping_price) || 0;
    }

    static get gift() {
        const base = window.free_shipping_gift || { enabled: false, threshold: 0, name: '' };
        const now = Date.now();
        let active = null;

        (base.schedule || []).forEach((entry) => {
            const start = FreeShippingMeter.startsAt(entry.from);
            if (isNaN(start) || start > now) return;
            if (!active || start >= active.start) active = { ...entry, start };
        });

        if (!active) return base;

        return {
            enabled: active.enabled === true,
            threshold: active.threshold || 0,
            name: active.name || '',
            autoAdd: false,
            variantId: active.variantId || 0
        };
    }
    static sectionsToRender = ['cart-drawer', 'cart-icon-bubble'];
    static giftSyncBusy = false;
    static giftSyncFailed = false;

    static get giftEnabled() {
        return FreeShippingMeter.gift.enabled === true && FreeShippingMeter.gift.threshold > FreeShippingMeter.freeshipPrice;
    }

    /* Theme settings store both thresholds in the shop currency (EUR).
       cart.total_price comes in the customer's presentment currency (PLN, CHF, GBP...),
       so the thresholds have to be converted before any comparison. */
    static get rate() {
        const rate = parseFloat(window.Shopify?.currency?.rate);
        return rate > 0 ? rate : 1;
    }

    static toPresentment(amount) {
        const rate = FreeShippingMeter.rate;
        return rate === 1 ? amount : Math.ceil(amount * rate);
    }

    static get freeshipGoal() {
        return FreeShippingMeter.toPresentment(FreeShippingMeter.freeshipPrice);
    }

    static get giftGoal() {
        return FreeShippingMeter.toPresentment(FreeShippingMeter.gift.threshold);
    }

    static get goal() {
        return FreeShippingMeter.giftEnabled ? FreeShippingMeter.giftGoal : FreeShippingMeter.freeshipGoal;
    }

    /* Milestone labels are whole amounts (80€, 356 zł), so decimals are dropped. */
    static formatGoal(amount) {
        const format = (window.money_format || '€{{amount}}')
            .replace(/amount_with_comma_separator/, 'amount_no_decimals_with_comma_separator')
            .replace(/\{\{\s*amount\s*\}\}/, '{{amount_no_decimals}}');

        return formatMoney(amount * 100, format);
    }

    static get giftName() {
        return FreeShippingMeter.gift.name || FreeShippingMeter.texts.gift_name_default;
    }

    connectedCallback() {

        this.progressBar = this.querySelector('[data-shipping-progress]');
        this.messageElement = this.querySelector('[data-shipping-message]');
        this.progressMeter = this.querySelector('[data-free-shipping-progress-meter]');
        this.shippingStop = this.querySelector('[data-shipping-stop]');
        this.giftStop = this.querySelector('[data-gift-stop]');

        this.textEnabled = this.progressBar?.dataset.textEnabled === 'true';

        this.loadCart();

        document.addEventListener('cart:updated', () => {
            this.loadCart();
        });

        /* The page stayed open across a schedule change (e.g. midnight of day 2). */
        document.addEventListener('campaign:changed', () => {
            this.loadCart();
        });

        this.addEventListener('change', () => {
            this.loadCart();
        });
    }

    async loadCart() {
        try {
            const res = await fetch('/cart.js');
            const cart = await res.json();
            this.update(cart);
        } catch (e) {
            console.error('Cart error:', e);
        }
    }

    update(cart) {

        const cartTotal = this.getQualifyingTotal(cart);
        const goal = FreeShippingMeter.goal;
        const percent = goal > 0 ? Math.min((cartTotal * 100) / goal, 100) : 0;

        const text = this.getText(cartTotal);
        const classLabel = this.getClassLabel(percent);

        this.render(percent, text, classLabel, cartTotal);

        this.syncGift(cart, cartTotal);
    }

    /* The gift line must not count towards the threshold that adds it,
       otherwise adding it can push the cart over or under the goal on its own. */
    getQualifyingTotal(cart) {

        let total = cart.total_price;

        (cart.items || []).forEach((item) => {
            if (FreeShippingMeter.isGiftLine(item)) total -= item.final_line_price;
        });

        return total / 100;
    }

    static isGiftLine(item) {
        return item.variant_id === FreeShippingMeter.gift.variantId && !!(item.properties && item.properties._free_gift);
    }

    async syncGift(cart, cartTotal) {

        const gift = FreeShippingMeter.gift;

        if (!FreeShippingMeter.giftEnabled || !gift.autoAdd || !gift.variantId) return;
        if (FreeShippingMeter.giftSyncBusy || FreeShippingMeter.giftSyncFailed) return;

        const giftLine = (cart.items || []).find(FreeShippingMeter.isGiftLine);
        const qualifies = cartTotal >= FreeShippingMeter.giftGoal;

        if (qualifies === !!giftLine) return;

        FreeShippingMeter.giftSyncBusy = true;

        try {
            const state = qualifies ? await this.addGift() : await this.removeGift(giftLine);

            if (state.status) {
                console.warn('Free gift could not be synced:', state.description || state.message);
                FreeShippingMeter.giftSyncFailed = true;
                return;
            }

            this.renderCartSections(state);
        } catch (e) {
            console.error('Free gift sync error:', e);
            FreeShippingMeter.giftSyncFailed = true;
        } finally {
            FreeShippingMeter.giftSyncBusy = false;
        }
    }

    addGift() {
        const body = JSON.stringify({
            items: [{ id: FreeShippingMeter.gift.variantId, quantity: 1, properties: { _free_gift: 'true' } }],
            sections: FreeShippingMeter.sectionsToRender,
            sections_url: window.location.pathname
        });

        return fetch(`${routes.cart_add_url}.js`, { ...fetchConfig(), ...{ body } }).then((r) => r.json());
    }

    removeGift(giftLine) {
        const body = JSON.stringify({
            id: giftLine.key,
            quantity: 0,
            sections: FreeShippingMeter.sectionsToRender,
            sections_url: window.location.pathname
        });

        return fetch(`${routes.cart_change_url}.js`, { ...fetchConfig(), ...{ body } }).then((r) => r.json());
    }

    /* Repaints the drawer in place. Unlike CartDrawer.renderContents this never
       opens the drawer, so a gift added on page load stays silent. */
    renderCartSections(state) {

        if (!state.sections) return;

        const parser = new DOMParser();

        const drawerHTML = state.sections['cart-drawer'];
        const drawerTarget = document.getElementById('CartDrawer');

        if (drawerHTML && drawerTarget) {
            const source = parser.parseFromString(drawerHTML, 'text/html').querySelector('#CartDrawer');
            if (source) drawerTarget.innerHTML = source.innerHTML;
        }

        const bubbleHTML = state.sections['cart-icon-bubble'];
        const bubbleTarget = document.getElementById('cart-icon-bubble');

        if (bubbleHTML && bubbleTarget) {
            const source = parser.parseFromString(bubbleHTML, 'text/html').querySelector('.shopify-section');
            if (source) bubbleTarget.innerHTML = source.innerHTML;
        }

        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof state.item_count === 'number') {
            drawer.classList.toggle('is-empty', state.item_count === 0);
        }
    }

    getText(cartTotal) {

        const t = FreeShippingMeter.texts;

        if (cartTotal === 0) {
            this.progressBar.classList.add('progress-hidden');

            return `<span>${t.free_shipping_message} ${formatMoney(FreeShippingMeter.freeshipGoal * 100, window.money_format)}!</span>`;
        }

        this.progressBar.classList.remove('progress-hidden');

        if (cartTotal >= FreeShippingMeter.freeshipGoal) {

            if (!FreeShippingMeter.giftEnabled) {
                return t.free_shipping_message_1;
            }

            if (cartTotal >= FreeShippingMeter.giftGoal) {
                return `<b>${t.gift_message_achieved}</b>`;
            }

            const giftRemain = FreeShippingMeter.giftGoal - cartTotal;

            return `
            <b>${t.gift_message_1}</b>
            <span>${t.gift_message_2} ${formatMoney(giftRemain * 100, window.money_format)} ${t.gift_message_3} ${FreeShippingMeter.giftName}</span>
            `;
        }

        const remain = FreeShippingMeter.freeshipGoal - cartTotal;

        return `
        <span>${t.free_shipping_message_2}</span>
        <b>${formatMoney(remain * 100, window.money_format)}</b>
        <span>${t.free_shipping_message_3}</span>
        <span class="text">${t.free_shipping_message_4}</span>
        `;
    }

    getClassLabel(percent) {

        if (percent === 0) return 'none';
        if (percent <= 30) return 'progress-30';
        if (percent <= 60) return 'progress-60';
        if (percent < 100) return 'progress-100';

        return 'progress-free';
    }

    render(percent, text, classLabel, cartTotal) {

        this.progressBar.classList.remove(
            'progress-30',
            'progress-60',
            'progress-100',
            'progress-free'
        );

        this.progressBar.classList.add(classLabel);

        this.progressMeter.style.width = `${percent}%`;

        const el = this.progressBar.querySelector('.text');
        if (el) el.innerHTML = `${percent.toFixed(0)}%`;
        this.messageElement.innerHTML = text;

        this.renderMilestones(cartTotal);

        this.convertCurrency();
    }

    renderMilestones(cartTotal) {

        const shippingReached = cartTotal >= FreeShippingMeter.freeshipGoal;
        const giftReached = FreeShippingMeter.giftEnabled && cartTotal >= FreeShippingMeter.giftGoal;

        this.classList.toggle('is-shipping-unlocked', shippingReached);
        this.classList.toggle('is-gift-unlocked', giftReached);

        this.shippingStop?.classList.toggle('is-reached', shippingReached);
        this.giftStop?.classList.toggle('is-reached', giftReached);

        if (this.giftStop) this.giftStop.hidden = !FreeShippingMeter.giftEnabled;

        this.renderMilestoneLabels();
    }

    /* Liquid prints the EUR labels only for EUR carts. The labels are always refreshed
       here: converted amounts for other currencies, scheduled thresholds for all. */
    renderMilestoneLabels() {

        const values = {
            start: 0,
            shipping: FreeShippingMeter.freeshipGoal,
            gift: FreeShippingMeter.giftGoal
        };

        /* EUR keeps the "80€" style Liquid prints; thresholds can change with the schedule. */
        const isShopCurrency = FreeShippingMeter.rate === 1;

        this.querySelectorAll('[data-milestone-label]').forEach((el) => {
            const key = el.dataset.milestoneLabel;
            if (!(key in values)) return;
            el.textContent = isShopCurrency ? `${Math.round(values[key])}€` : FreeShippingMeter.formatGoal(values[key]);
        });
    }

    convertCurrency() {

        if (!document.querySelector('.dropdown-item[data-currency]')) return;

        if (
            (window.show_multiple_currencies &&
                typeof Currency !== 'undefined' &&
                Currency.currentCurrency !== shopCurrency) ||
            window.show_auto_currency
        ) {
            Currency.convertAll(
                window.shop_currency,
                document.querySelector('#currencies .active')?.dataset.currency,
                'span.money',
                'money_format'
            );
        }
    }
}

customElements.define('free-shipping-component', FreeShippingMeter);
